import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Skeleton } from "@mui/material";
import dayjs from "dayjs";
import { PayByLinkDetailsStyled, CancelPayByLinkButton } from "./styled";
import {
  CustomBreadcrumb,
  InfoLabel,
  InfoValue,
  StatusBadge,
  CurrencyLabel,
  ConfirmationModal,
  SnackMsg,
} from "../../components";
import {
  PAY_BY_LINK_CONSTANTS,
  PRODUCT_HEADERS,
} from "../../config/constants";
import { ROUTES } from "../../config/routes";
import { CopyTextLogo } from "assets/icons";
import { API_URL } from "utils/Constants";
import { ApiService } from "utils/ApiService";

// Helper function to map error codes to user-friendly messages
const getErrorMessage = (errorCode) => {
  switch (String(errorCode)) {
    case "102":
      return PAY_BY_LINK_CONSTANTS.ERROR_NOT_FOUND;
    case "402":
      return PAY_BY_LINK_CONSTANTS.ERROR_ACCESS_DENIED;
    case "401":
      return PAY_BY_LINK_CONSTANTS.ERROR_CANCEL_NOT_ALLOWED;
    default:
      return PAY_BY_LINK_CONSTANTS.CANCEL_ERROR;
  }
};

const PayByLinkDetails = () => {
  const { ott } = useParams();
  const navigate = useNavigate();
  const [checkoutData, setCheckoutData] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelErrorMsg, setCancelErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const fetchCheckoutDetail = useCallback(async () => {
    setIsLoading(true);
    try {
      const url = `${API_URL.MANAGE_CHECKOUT_DETAILS}${ott}`;
      const response = await ApiService.getRequest(url, PRODUCT_HEADERS);
      if (response?.status?.code === "Y" && response.body) {
        setCheckoutData(response.body);
      } else {
        const errorMsg = getErrorMessage(response?.status?.errorCode);
        sessionStorage.setItem("flashMsg", errorMsg);
        navigate(ROUTES.PAY_BY_LINK, { replace: true });
      }
    } catch (err) {
      navigate(ROUTES.PAY_BY_LINK, { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [ott, navigate]);

  useEffect(() => {
    fetchCheckoutDetail();
  }, [fetchCheckoutDetail]);

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    setIsCancelling(true);
    setCancelErrorMsg("");
    try {
      const payload = { ott };
      const response = await ApiService.postRequest(
        API_URL.MANAGE_CHECKOUT_CANCEL,
        payload,
        PRODUCT_HEADERS,
      );
      if (response?.status?.code === "Y") {
        setShowCancelModal(false);
        // Navigate with success message to show in list
        sessionStorage.setItem("flashMsg", PAY_BY_LINK_CONSTANTS.CANCEL_SUCCESS);
        navigate(ROUTES.PAY_BY_LINK, { replace: true });
      } else {
        const errorMsg = response?.status?.message || getErrorMessage(response?.status?.errorCode);
        setCancelErrorMsg(errorMsg);
      }
    } catch (error) {
      setCancelErrorMsg(error?.message || PAY_BY_LINK_CONSTANTS.CANCEL_ERROR);
    } finally {
      setIsCancelling(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (value) => {
    if (!value) return "";
    const d = dayjs(value);
    return d.isValid() ? d.format("DD/MM/YYYY") : value;
  };

  if (isLoading) {
    return (
      <CustomBreadcrumb
        header={PAY_BY_LINK_CONSTANTS.HEADER}
        currentPage={PAY_BY_LINK_CONSTANTS.BREADCRUMB}
        isFromLandingPage={false}
        showBackButton={true}
        onBackClick={() => navigate(-1)}
      >
        <PayByLinkDetailsStyled>
          <div className="section-part">
            <Skeleton
              variant="rectangular"
              width={150}
              height={24}
              style={{ marginBottom: "16px" }}
            />
            <Skeleton variant="rectangular" width="100%" height={200} />
          </div>
          <div className="section-part">
            <Skeleton
              variant="rectangular"
              width={150}
              height={24}
              style={{ marginBottom: "16px" }}
            />
            <Skeleton variant="rectangular" width="100%" height={150} />
          </div>
          <div className="section-part">
            <Skeleton
              variant="rectangular"
              width={150}
              height={24}
              style={{ marginBottom: "16px" }}
            />
            <Skeleton variant="rectangular" width="100%" height={150} />
          </div>
          <div className="section-part">
            <Skeleton
              variant="rectangular"
              width={150}
              height={24}
              style={{ marginBottom: "16px" }}
            />
            <Skeleton variant="rectangular" width="100%" height={100} />
          </div>
        </PayByLinkDetailsStyled>
      </CustomBreadcrumb>
    );
  }

  if (!checkoutData) return null;

  const canCancelLink =
    checkoutData.linkStatus === "UNUSED_OTT" && !isCancelling;

  return (
    <CustomBreadcrumb
      header={PAY_BY_LINK_CONSTANTS.BREADCRUMB}
      currentPage={PAY_BY_LINK_CONSTANTS.BREADCRUMB}
      isFromLandingPage={false}
      showBackButton={true}
      onBackClick={() => navigate(-1)}
      rightContent={
        canCancelLink ? (
          <CancelPayByLinkButton
            onClick={handleCancelClick}
            bsPrefix="cancel-plan-btn"
          >
            {PAY_BY_LINK_CONSTANTS.CANCEL_PAYMENT_LINK}
          </CancelPayByLinkButton>
        ) : null
      }
    >
      <PayByLinkDetailsStyled>
        <SnackMsg
          showSnack={Boolean(cancelErrorMsg)}
          snackMsg={cancelErrorMsg}
          onClose={() => setCancelErrorMsg("")}
        />

        {/* Payment Details Section */}
        <div className="section-part">
          <h3 className="section-title">
            {PAY_BY_LINK_CONSTANTS.DETAIL_PAYMENT_DETAILS}
          </h3>
          <div className="info-grid">
            {/* maybe use in future  */}
            {/* {checkoutData.paymentUrl && (
              <div className="info-item">
                <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_URL}</InfoLabel>
                <div className="url-container">
                  <InfoValue>{checkoutData.paymentUrl}</InfoValue>
                  <button
                    className="copy-button"
                    onClick={() => copyToClipboard(checkoutData.paymentUrl)}
                    title="Copy URL"
                  >
                    <CopyTextLogo />
                  </button>
                </div>
              </div>
            )} */}
            <div className="info-item">
              <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_ORDER_ID}</InfoLabel>
              <InfoValue>{checkoutData.orderInfo?.orderId}</InfoValue>
            </div>
            <div className="info-item">
              <InfoLabel>
                {PAY_BY_LINK_CONSTANTS.DETAIL_LINK_STATUS}
              </InfoLabel>
              <InfoValue>
                <StatusBadge status={checkoutData.linkStatus} />
              </InfoValue>
            </div>
            <div className="info-item">
              <InfoLabel>
                {PAY_BY_LINK_CONSTANTS.DETAIL_PAYMENT_STATUS}
              </InfoLabel>
              <InfoValue>
                <StatusBadge status={checkoutData.paymentStatus} />
              </InfoValue>
            </div>
            <div className="info-item">
              <InfoLabel>
                {PAY_BY_LINK_CONSTANTS.DETAIL_EXPIRY_DATE}
              </InfoLabel>
              <InfoValue>{formatDate(checkoutData.expiryDate)}</InfoValue>
            </div>
            <div className="info-item">
              <InfoLabel>
                {PAY_BY_LINK_CONSTANTS.DETAIL_DATE_CREATED}
              </InfoLabel>
              <InfoValue>{formatDate(checkoutData.createdAt)}</InfoValue>
            </div>
            <div className="info-item">
              <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_AMOUNT}</InfoLabel>
              <InfoValue>
                <CurrencyLabel amount={checkoutData.orderInfo?.netTransactionAmount ?? 0} />
              </InfoValue>
            </div>
            {checkoutData.orderInfo?.currency && (
              <div className="info-item">
                <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_CURRENCY}</InfoLabel>
                <InfoValue>{checkoutData.orderInfo.currency}</InfoValue>
              </div>
            )}
          </div>
        </div>

        {/* Transaction Details Section */}
        <div className="section-part">
          <h3 className="section-title">
            {PAY_BY_LINK_CONSTANTS.DETAIL_TRANSACTION_DETAILS}
          </h3>
          {(() => {
            const pi = checkoutData.paymentInfos?.[0];
            if (!pi) return (
              <div className="info-grid">
                <InfoValue>No payment attempts recorded</InfoValue>
              </div>
            );
            return (
              <div className="info-grid">
                {pi.transactionId && (
                  <div className="info-item">
                    <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_TRANSACTION_ID}</InfoLabel>
                    <InfoValue>{pi.transactionId}</InfoValue>
                  </div>
                )}
                {pi.maskedCardNumber && (
                  <div className="info-item">
                    <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_MASKED_CARD}</InfoLabel>
                    <InfoValue>{pi.maskedCardNumber}</InfoValue>
                  </div>
                )}
                {pi.cardIssuer && (
                  <div className="info-item">
                    <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_CARD_ISSUER}</InfoLabel>
                    <InfoValue>{pi.cardIssuer}</InfoValue>
                  </div>
                )}
                {pi.cardBrand && (
                  <div className="info-item">
                    <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_CARD_BRAND}</InfoLabel>
                    <InfoValue>{pi.cardBrand}</InfoValue>
                  </div>
                )}
                {pi.sourceOfFunds && (
                  <div className="info-item">
                    <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_SOURCE_OF_FUNDS}</InfoLabel>
                    <InfoValue>{pi.sourceOfFunds}</InfoValue>
                  </div>
                )}
                {pi.rrn && (
                  <div className="info-item">
                    <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_RRN}</InfoLabel>
                    <InfoValue>{pi.rrn}</InfoValue>
                  </div>
                )}
                {pi.eppRequested && (
                  <>
                    {pi.eppProvider && (
                      <div className="info-item">
                        <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_EPP_PROVIDER}</InfoLabel>
                        <InfoValue>{pi.eppProvider}</InfoValue>
                      </div>
                    )}
                    {pi.eppPlanId && (
                      <div className="info-item">
                        <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_EPP_PLAN_ID}</InfoLabel>
                        <InfoValue>{pi.eppPlanId}</InfoValue>
                      </div>
                    )}
                  </>
                )}
                {!pi.successful && pi.failureReason && (
                  <div className="info-item">
                    <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_FAILURE_REASON}</InfoLabel>
                    <InfoValue>{pi.failureReason}</InfoValue>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Customer Details Section */}
        <div className="section-part">
          <h3 className="section-title">
            {PAY_BY_LINK_CONSTANTS.DETAIL_CUSTOMER_DETAILS}
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <InfoLabel>
                {PAY_BY_LINK_CONSTANTS.DETAIL_CUSTOMER_NAME}
              </InfoLabel>
              <InfoValue>{checkoutData.customerInfo?.name}</InfoValue>
            </div>
            <div className="info-item">
              <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_EMAIL}</InfoLabel>
              <InfoValue>{checkoutData.customerInfo?.email}</InfoValue>
            </div>
            {checkoutData.customerInfo?.additionalFields?.map((field, index) => (
              <div key={index} className="info-item">
                <InfoLabel>{field.title}</InfoLabel>
                <InfoValue>{field.value}</InfoValue>
              </div>
            ))}
          </div>
        </div>

        {/* Product Details Section */}
        <div className="section-part">
          <h3 className="section-title">
            {PAY_BY_LINK_CONSTANTS.DETAIL_PRODUCT_DETAILS}
          </h3>
          {checkoutData.orderInfo?.items?.map((item, index) => (
            <div key={index} className={`info-grid product-item ${index !== 0 ? "mt-4" : ""}`}>
              <div className="info-item">
                <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_ITEM_TITLE}</InfoLabel>
                <InfoValue>{item.title}</InfoValue>
              </div>
              {item.description && (
                <div className="info-item">
                  <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_ITEM_DESCRIPTION}</InfoLabel>
                  <InfoValue>{item.description}</InfoValue>
                </div>
              )}
              <div className="info-item">
                <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_ITEM_QUANTITY}</InfoLabel>
                <InfoValue>{item.quantity}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{PAY_BY_LINK_CONSTANTS.DETAIL_ITEM_AMOUNT}</InfoLabel>
                <InfoValue>
                  <CurrencyLabel amount={item.amount ?? 0} />
                </InfoValue>
              </div>
            </div>
          ))}
        </div>

        <ConfirmationModal
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          onConfirm={handleCancelConfirm}
          title={PAY_BY_LINK_CONSTANTS.CANCEL_LINK_TITLE}
          message={`${PAY_BY_LINK_CONSTANTS.CANCEL_LINK_MESSAGE}`}
          confirmLabel={PAY_BY_LINK_CONSTANTS.CANCEL_LINK_CONFIRM}
          cancelLabel={PAY_BY_LINK_CONSTANTS.CANCEL_LINK_BACK}
          isLoading={isCancelling}
        />
      </PayByLinkDetailsStyled>
    </CustomBreadcrumb>
  );
};

export default PayByLinkDetails;
